import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarDays,
  Loader2,
  PencilLine,
  UserRound,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import { ROLE_KEYS, normalizeRole } from '../utils/rolePermissions';
import {
  checkInAttendance,
  checkOutAttendance,
  correctAttendance,
  getAttendanceDaily,
  getAttendanceMonthly,
  getAttendanceStaffHistory,
  getAttendanceStaffOptions,
  markAttendance,
  markAttendanceAbsent,
  markAttendanceHoliday,
  markAttendanceLeave,
} from '../services/staffAttendanceApi';

const STATUS_OPTIONS = ['present', 'absent', 'half_day', 'leave', 'holiday'];

function getTodayDate() {
  return new Date().toISOString().split('T')[0];
}

function formatDate(dateValue) {
  if (!dateValue) return '-';
  const parsed = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(dateValue);
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
}

function formatDateTime(dateTime) {
  if (!dateTime) return '-';
  const parsed = new Date(dateTime);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(parsed);
}

function statusLabel(status) {
  return String(status || '-')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusClass(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'present') return 'pill-status pill-green';
  if (value === 'half_day') return 'pill-status pill-yellow';
  if (value === 'holiday') return 'pill-status pill-yellow';
  if (value === 'leave') return 'pill-status pill-red';
  if (value === 'absent') return 'pill-status pill-red';
  return 'pill-status bg-slate-100 text-slate-700';
}

function formatMinutes(minutes) {
  const total = Math.max(0, Number(minutes || 0));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (!hours) return `${mins}m`;
  if (!mins) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function toMonthYearFromDate(dateValue) {
  const parsed = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    const now = new Date();
    return {
      month: now.getMonth() + 1,
      year: now.getFullYear(),
    };
  }
  return {
    month: parsed.getMonth() + 1,
    year: parsed.getFullYear(),
  };
}

function parseFriendlyError(error) {
  const status = error?.response?.status;
  const backendMessage = error?.response?.data?.error || error?.message || 'Request failed.';

  if (status === 403) return backendMessage || 'You do not have permission for this action.';
  if (status === 404) return backendMessage || 'Record not found.';
  if (status === 409) return backendMessage || 'This record is locked or conflicting.';
  if (status === 400) return backendMessage || 'Please check the entered values and try again.';
  if (status === 401) return 'Your session expired. Please login again.';
  return backendMessage;
}

function buildDateTimeFromDateAndClock(dateOnly, clockValue) {
  if (!clockValue) return null;
  return `${dateOnly}T${clockValue}:00`;
}

function CorrectionModal({ open, row, onClose, onSave, saving }) {
  const [status, setStatus] = useState('present');
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !row) return;
    setStatus(String(row.status || 'present'));
    setCheckInTime(row.checkInAt ? new Date(row.checkInAt).toISOString().slice(11, 16) : '');
    setCheckOutTime(row.checkOutAt ? new Date(row.checkOutAt).toISOString().slice(11, 16) : '');
    setNotes(row.notes || '');
    setReason('');
  }, [open, row]);

  if (!open || !row) return null;

  const submit = () => {
    if (!String(reason || '').trim()) {
      toast.error('Correction reason is required.');
      return;
    }

    const updates = {
      status,
      notes,
      checkInAt: buildDateTimeFromDateAndClock(row.attendanceDate, checkInTime),
      checkOutAt: buildDateTimeFromDateAndClock(row.attendanceDate, checkOutTime),
    };

    onSave({
      attendanceId: row.id,
      correctionType: 'manual_correction',
      reason: reason.trim(),
      updates,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
      <div className="w-full max-w-xl rounded-[24px] border border-slate-200 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.28)]">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900">Correct Attendance</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-900">{row?.StaffUser?.name || 'Staff'}</span> | {formatDate(row?.attendanceDate)}
            </p>
          </div>

          <div>
            <label className="label-modern">Status</label>
            <select className="input-modern" value={status} onChange={(event) => setStatus(event.target.value)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {statusLabel(option)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="label-modern">Check-in Time</label>
              <input
                type="time"
                className="input-modern"
                value={checkInTime}
                onChange={(event) => setCheckInTime(event.target.value)}
              />
            </div>
            <div>
              <label className="label-modern">Check-out Time</label>
              <input
                type="time"
                className="input-modern"
                value={checkOutTime}
                onChange={(event) => setCheckOutTime(event.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="label-modern">Notes</label>
            <textarea
              className="textarea-modern"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional notes"
            />
          </div>

          <div>
            <label className="label-modern">Correction Reason *</label>
            <textarea
              className="textarea-modern"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Explain why this correction is needed"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={submit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Correction'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StaffAttendance() {
  const user = useAuthStore((state) => state.user);
  const roleKey = normalizeRole(user?.role);
  const userId = Number(user?.id || 0);

  const canAccess = [ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.DOCTOR, ROLE_KEYS.STAFF].includes(roleKey);
  const canManageTeam = [ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.DOCTOR].includes(roleKey);
  const canCorrect = roleKey === ROLE_KEYS.OWNER_ADMIN;
  const isStaff = roleKey === ROLE_KEYS.STAFF;

  const [activeTab, setActiveTab] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const initialMonthYear = toMonthYearFromDate(getTodayDate());
  const [selectedMonth, setSelectedMonth] = useState(String(initialMonthYear.month));
  const [selectedYear, setSelectedYear] = useState(String(initialMonthYear.year));
  const [selectedStaffId, setSelectedStaffId] = useState('');

  const [staffOptions, setStaffOptions] = useState([]);
  const [dailyPayload, setDailyPayload] = useState({ date: getTodayDate(), count: 0, rows: [] });
  const [historyPayload, setHistoryPayload] = useState({ count: 0, rows: [], limit: 30, offset: 0 });
  const [monthlyPayload, setMonthlyPayload] = useState({ totals: null, byStaff: [], rowCount: 0 });

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [quickNotes, setQuickNotes] = useState('');
  const [quickReason, setQuickReason] = useState('');

  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [correctionRow, setCorrectionRow] = useState(null);
  const [correctionSaving, setCorrectionSaving] = useState(false);

  const resolvedStaffId = useMemo(() => {
    if (isStaff) return userId ? String(userId) : '';
    return selectedStaffId;
  }, [isStaff, selectedStaffId, userId]);

  const selectedStaffName = useMemo(() => {
    if (!resolvedStaffId) return '';
    const found = staffOptions.find((row) => String(row.id) === String(resolvedStaffId));
    return found?.name || '';
  }, [resolvedStaffId, staffOptions]);

  const missingCheckoutCount = useMemo(
    () => (dailyPayload.rows || []).filter((row) => row.missingCheckout).length,
    [dailyPayload.rows]
  );

  const canShowStaffPicker = canManageTeam && staffOptions.length > 0;

  const loadStaffOptions = useCallback(async () => {
    if (!canAccess) return;
    const payload = await getAttendanceStaffOptions();
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    setStaffOptions(rows);

    if (isStaff) {
      setSelectedStaffId(userId ? String(userId) : '');
      return;
    }

    if (!selectedStaffId && rows.length > 0) {
      setSelectedStaffId(String(rows[0].id));
    }
  }, [canAccess, isStaff, selectedStaffId, userId]);

  const loadDaily = useCallback(async () => {
    if (!canAccess) return;
    setLoadingDaily(true);
    try {
      const params = { date: selectedDate };
      if (!isStaff && resolvedStaffId) {
        params.staffUserId = resolvedStaffId;
      }
      const payload = await getAttendanceDaily(params);
      setDailyPayload({
        date: payload?.date || selectedDate,
        count: Number(payload?.count || 0),
        rows: Array.isArray(payload?.rows) ? payload.rows : [],
      });
    } catch (error) {
      toast.error(parseFriendlyError(error));
    } finally {
      setLoadingDaily(false);
    }
  }, [canAccess, isStaff, resolvedStaffId, selectedDate]);

  const loadHistory = useCallback(async () => {
    if (!canAccess || !resolvedStaffId) {
      setHistoryPayload({ count: 0, rows: [], limit: 30, offset: 0 });
      return;
    }
    setLoadingHistory(true);
    try {
      const payload = await getAttendanceStaffHistory(resolvedStaffId, {
        startDate: '',
        endDate: '',
        limit: historyPayload.limit || 30,
        offset: historyPayload.offset || 0,
      });

      setHistoryPayload({
        count: Number(payload?.count || 0),
        rows: Array.isArray(payload?.rows) ? payload.rows : [],
        limit: Number(payload?.limit || 30),
        offset: Number(payload?.offset || 0),
      });
    } catch (error) {
      toast.error(parseFriendlyError(error));
    } finally {
      setLoadingHistory(false);
    }
  }, [canAccess, historyPayload.limit, historyPayload.offset, resolvedStaffId]);

  const loadMonthly = useCallback(async () => {
    if (!canAccess) return;
    setLoadingMonthly(true);
    try {
      const params = {
        month: selectedMonth,
        year: selectedYear,
      };
      if (!isStaff && resolvedStaffId) {
        params.staffUserId = resolvedStaffId;
      }
      const payload = await getAttendanceMonthly(params);
      setMonthlyPayload({
        totals: payload?.totals || null,
        byStaff: Array.isArray(payload?.byStaff) ? payload.byStaff : [],
        rowCount: Number(payload?.rowCount || 0),
      });
    } catch (error) {
      toast.error(parseFriendlyError(error));
    } finally {
      setLoadingMonthly(false);
    }
  }, [canAccess, isStaff, resolvedStaffId, selectedMonth, selectedYear]);

  const initializePage = useCallback(async () => {
    setLoadingPage(true);
    try {
      await loadStaffOptions();
    } catch (error) {
      toast.error(parseFriendlyError(error));
    } finally {
      setLoadingPage(false);
    }
  }, [loadStaffOptions]);

  useEffect(() => {
    initializePage();
  }, [initializePage]);

  useEffect(() => {
    if (loadingPage) return;
    loadDaily();
  }, [loadingPage, loadDaily]);

  useEffect(() => {
    if (loadingPage) return;
    loadMonthly();
  }, [loadingPage, loadMonthly]);

  useEffect(() => {
    if (loadingPage) return;
    loadHistory();
  }, [loadingPage, loadHistory]);

  const refreshAll = async () => {
    await Promise.all([loadDaily(), loadMonthly(), loadHistory()]);
  };

  const runAction = async (key, actionFn) => {
    setActionLoading(key);
    try {
      await actionFn();
      await refreshAll();
    } catch (error) {
      toast.error(parseFriendlyError(error));
    } finally {
      setActionLoading('');
    }
  };

  const requireStaffForAction = () => {
    if (!resolvedStaffId) {
      toast.error('Please select a staff member first.');
      return false;
    }
    return true;
  };

  const handleQuickMark = async (status) => {
    if (!requireStaffForAction()) return;
    await runAction(`mark-${status}`, async () => {
      if (status === 'absent') {
        await markAttendanceAbsent({
          staffUserId: Number(resolvedStaffId),
          attendanceDate: selectedDate,
          notes: quickNotes || null,
          reason: quickReason || null,
        });
      } else if (status === 'leave') {
        await markAttendanceLeave({
          staffUserId: Number(resolvedStaffId),
          attendanceDate: selectedDate,
          notes: quickNotes || null,
          reason: quickReason || null,
        });
      } else if (status === 'holiday') {
        await markAttendanceHoliday({
          staffUserId: Number(resolvedStaffId),
          attendanceDate: selectedDate,
          notes: quickNotes || null,
          reason: quickReason || null,
        });
      } else {
        await markAttendance({
          staffUserId: Number(resolvedStaffId),
          attendanceDate: selectedDate,
          status,
          notes: quickNotes || null,
          reason: quickReason || null,
        });
      }
      toast.success(`Marked ${statusLabel(status)}.`);
    });
  };

  const handleQuickCheckIn = async () => {
    if (!requireStaffForAction()) return;
    await runAction('checkin', async () => {
      await checkInAttendance({
        staffUserId: Number(resolvedStaffId),
        attendanceDate: selectedDate,
        notes: quickNotes || null,
      });
      toast.success('Check-in recorded.');
    });
  };

  const handleQuickCheckOut = async () => {
    if (!requireStaffForAction()) return;
    await runAction('checkout', async () => {
      await checkOutAttendance({
        staffUserId: Number(resolvedStaffId),
        attendanceDate: selectedDate,
        notes: quickNotes || null,
        reason: quickReason || null,
      });
      toast.success('Check-out recorded.');
    });
  };

  const handleRowCheckIn = async (row) => {
    await runAction(`row-checkin-${row.id}`, async () => {
      await checkInAttendance({
        staffUserId: Number(row.staffUserId),
        attendanceDate: row.attendanceDate,
      });
      toast.success(`Check-in updated for ${row?.StaffUser?.name || 'staff'}.`);
    });
  };

  const handleRowCheckOut = async (row) => {
    await runAction(`row-checkout-${row.id}`, async () => {
      await checkOutAttendance({
        staffUserId: Number(row.staffUserId),
        attendanceDate: row.attendanceDate,
      });
      toast.success(`Check-out updated for ${row?.StaffUser?.name || 'staff'}.`);
    });
  };

  const openCorrectionModal = (row) => {
    if (!canCorrect) {
      toast.error('Only Owner/Admin can correct attendance records.');
      return;
    }
    setCorrectionRow(row);
    setCorrectionModalOpen(true);
  };

  const saveCorrection = async (payload) => {
    setCorrectionSaving(true);
    try {
      await correctAttendance(payload);
      setCorrectionModalOpen(false);
      setCorrectionRow(null);
      toast.success('Attendance correction saved.');
      await refreshAll();
    } catch (error) {
      toast.error(parseFriendlyError(error));
    } finally {
      setCorrectionSaving(false);
    }
  };

  const goHistoryNext = () => {
    if (historyPayload.offset + historyPayload.limit >= historyPayload.count) return;
    setHistoryPayload((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const goHistoryPrev = () => {
    if (historyPayload.offset <= 0) return;
    setHistoryPayload((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  const tabButton = (id, label) => (
    <button
      type="button"
      key={id}
      className={activeTab === id ? 'btn-tab-active' : 'btn-tab'}
      onClick={() => setActiveTab(id)}
    >
      {label}
    </button>
  );

  if (!canAccess) {
    return (
      <div className="app-page">
        <div className="section-card p-6">
          <h1 className="page-title">Staff Attendance</h1>
          <p className="mt-2 text-sm text-rose-600">
            Your role does not have attendance access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page space-y-6">
      <div className="section-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="page-title">Staff Attendance</h1>
            <p className="page-subtitle">
              Track staff presence, check-in, checkout, late minutes, overtime, leave, and monthly attendance.
            </p>
            {isStaff ? (
              <p className="mt-2 text-sm font-semibold text-slate-600">
                You are viewing your own attendance records.
              </p>
            ) : null}
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-semibold">
                Missing Checkout: {missingCheckoutCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="section-card p-5">
        <div className="flex flex-wrap gap-2">
          {tabButton('daily', 'Daily Attendance')}
          {tabButton('history', 'Staff History')}
          {tabButton('monthly', 'Monthly Summary')}
        </div>
      </div>

      {activeTab === 'daily' ? (
        <section className="section-card p-5 space-y-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <div>
              <label className="label-modern">Attendance Date</label>
              <div className="relative">
                <CalendarDays className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  type="date"
                  className="input-modern pl-10"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                />
              </div>
            </div>

            {canShowStaffPicker ? (
              <div>
                <label className="label-modern">Staff Member</label>
                <select
                  className="input-modern"
                  value={selectedStaffId}
                  onChange={(event) => setSelectedStaffId(event.target.value)}
                >
                  {staffOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({statusLabel(item.role)})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="label-modern">Staff Member</label>
                <div className="input-modern flex items-center gap-2 bg-slate-50">
                  <UserRound className="h-4 w-4 text-slate-500" />
                  <span>{selectedStaffName || user?.name || 'Current User'}</span>
                </div>
              </div>
            )}

            <div className="lg:col-span-2">
              <label className="label-modern">Notes (optional)</label>
              <input
                className="input-modern"
                value={quickNotes}
                onChange={(event) => setQuickNotes(event.target.value)}
                placeholder="Optional note for check-in / marking"
              />
            </div>
          </div>

          {canManageTeam || isStaff ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-900">Quick Actions</p>
              <p className="mt-1 text-xs text-slate-500">
                Use these actions to mark attendance and record check-in/check-out.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                <button
                  type="button"
                  className="btn-success"
                  onClick={() => handleQuickMark('present')}
                  disabled={actionLoading === 'mark-present' || loadingPage}
                >
                  Present
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => handleQuickMark('half_day')}
                  disabled={actionLoading === 'mark-half_day' || loadingPage}
                >
                  Half-day
                </button>
                {canManageTeam ? (
                  <>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleQuickMark('absent')}
                      disabled={actionLoading === 'mark-absent' || loadingPage}
                    >
                      Absent
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleQuickMark('leave')}
                      disabled={actionLoading === 'mark-leave' || loadingPage}
                    >
                      Leave
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleQuickMark('holiday')}
                      disabled={actionLoading === 'mark-holiday' || loadingPage}
                    >
                      Holiday
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleQuickCheckIn}
                  disabled={actionLoading === 'checkin' || loadingPage}
                >
                  {actionLoading === 'checkin' ? 'Checking...' : 'Check In'}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleQuickCheckOut}
                  disabled={actionLoading === 'checkout' || loadingPage}
                >
                  {actionLoading === 'checkout' ? 'Saving...' : 'Check Out'}
                </button>
              </div>
              {canCorrect ? (
                <div className="mt-3">
                  <label className="label-modern">Reason (for corrections/check-out updates)</label>
                  <input
                    className="input-modern"
                    value={quickReason}
                    onChange={(event) => setQuickReason(event.target.value)}
                    placeholder="Optional correction/context reason"
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="table-shell">
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Late</th>
                    <th>Overtime</th>
                    <th>Worked</th>
                    <th>Missing Checkout</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingDaily ? (
                    <tr>
                      <td colSpan={11}>
                        <div className="flex items-center gap-2 py-2 text-slate-500">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading daily attendance...
                        </div>
                      </td>
                    </tr>
                  ) : (dailyPayload.rows || []).length === 0 ? (
                    <tr>
                      <td colSpan={11}>No attendance records found for this date.</td>
                    </tr>
                  ) : (
                    dailyPayload.rows.map((row) => (
                      <tr key={row.id}>
                        <td className="font-semibold text-slate-900">{row?.StaffUser?.name || `User #${row.staffUserId}`}</td>
                        <td>{formatDate(row.attendanceDate)}</td>
                        <td>
                          <span className={statusClass(row.status)}>{statusLabel(row.status)}</span>
                        </td>
                        <td>{formatDateTime(row.checkInAt)}</td>
                        <td>{formatDateTime(row.checkOutAt)}</td>
                        <td>{formatMinutes(row.lateMinutes)}</td>
                        <td>{formatMinutes(row.overtimeMinutes)}</td>
                        <td>{formatMinutes(row.totalWorkedMinutes)}</td>
                        <td>
                          {row.missingCheckout ? (
                            <span className="pill-status pill-red">Missing</span>
                          ) : (
                            <span className="pill-status bg-emerald-100 text-emerald-700">No</span>
                          )}
                        </td>
                        <td className="max-w-[220px] truncate" title={row.notes || ''}>
                          {row.notes || '-'}
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn-secondary px-3 py-2 text-xs"
                              onClick={() => handleRowCheckIn(row)}
                              disabled={actionLoading === `row-checkin-${row.id}`}
                            >
                              Check In
                            </button>
                            <button
                              type="button"
                              className="btn-secondary px-3 py-2 text-xs"
                              onClick={() => handleRowCheckOut(row)}
                              disabled={actionLoading === `row-checkout-${row.id}`}
                            >
                              Check Out
                            </button>
                            {canCorrect ? (
                              <button
                                type="button"
                                className="btn-secondary px-3 py-2 text-xs"
                                onClick={() => openCorrectionModal(row)}
                              >
                                <PencilLine className="h-3.5 w-3.5" />
                                Correct
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === 'history' ? (
        <section className="section-card p-5 space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Staff History</h2>
              <p className="mt-1 text-sm text-slate-500">Attendance history by staff member.</p>
            </div>
            {canShowStaffPicker ? (
              <div className="w-full md:w-72">
                <label className="label-modern">Staff Member</label>
                <select
                  className="input-modern"
                  value={selectedStaffId}
                  onChange={(event) => {
                    setHistoryPayload((prev) => ({ ...prev, offset: 0 }));
                    setSelectedStaffId(event.target.value);
                  }}
                >
                  {staffOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({statusLabel(item.role)})
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          <div className="table-shell">
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Late</th>
                    <th>Overtime</th>
                    <th>Worked</th>
                    <th>Missing Checkout</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingHistory ? (
                    <tr>
                      <td colSpan={9}>
                        <div className="flex items-center gap-2 py-2 text-slate-500">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading staff history...
                        </div>
                      </td>
                    </tr>
                  ) : (historyPayload.rows || []).length === 0 ? (
                    <tr>
                      <td colSpan={9}>No history rows found.</td>
                    </tr>
                  ) : (
                    historyPayload.rows.map((row) => (
                      <tr key={row.id}>
                        <td>{formatDate(row.attendanceDate)}</td>
                        <td>
                          <span className={statusClass(row.status)}>{statusLabel(row.status)}</span>
                        </td>
                        <td>{formatDateTime(row.checkInAt)}</td>
                        <td>{formatDateTime(row.checkOutAt)}</td>
                        <td>{formatMinutes(row.lateMinutes)}</td>
                        <td>{formatMinutes(row.overtimeMinutes)}</td>
                        <td>{formatMinutes(row.totalWorkedMinutes)}</td>
                        <td>{row.missingCheckout ? 'Yes' : 'No'}</td>
                        <td>{row.notes || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="btn-secondary px-4 py-2"
              onClick={goHistoryPrev}
              disabled={historyPayload.offset <= 0}
            >
              Prev
            </button>
            <button
              type="button"
              className="btn-secondary px-4 py-2"
              onClick={goHistoryNext}
              disabled={historyPayload.offset + historyPayload.limit >= historyPayload.count}
            >
              Next
            </button>
          </div>
        </section>
      ) : null}

      {activeTab === 'monthly' ? (
        <section className="section-card p-5 space-y-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Monthly Summary</h2>
              <p className="mt-1 text-sm text-slate-500">
                Present/Absent/Leave/Half-day/Holiday and total timing metrics.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="label-modern">Month</label>
                <select
                  className="input-modern"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                >
                  {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                    <option key={month} value={String(month)}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-modern">Year</label>
                <input
                  className="input-modern"
                  type="number"
                  min="2000"
                  max="2100"
                  value={selectedYear}
                  onChange={(event) => setSelectedYear(event.target.value)}
                />
              </div>
              {canShowStaffPicker ? (
                <div>
                  <label className="label-modern">Staff (optional)</label>
                  <select
                    className="input-modern"
                    value={selectedStaffId}
                    onChange={(event) => setSelectedStaffId(event.target.value)}
                  >
                    {staffOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
            <div className="metric-card">
              <p className="metric-label">Present Days</p>
              <p className="metric-value">{Number(monthlyPayload?.totals?.totalPresentDays || 0)}</p>
            </div>
            <div className="metric-card">
              <p className="metric-label">Absent Days</p>
              <p className="metric-value">{Number(monthlyPayload?.totals?.totalAbsentDays || 0)}</p>
            </div>
            <div className="metric-card">
              <p className="metric-label">Leave Days</p>
              <p className="metric-value">{Number(monthlyPayload?.totals?.totalLeaveDays || 0)}</p>
            </div>
            <div className="metric-card">
              <p className="metric-label">Half-days</p>
              <p className="metric-value">{Number(monthlyPayload?.totals?.totalHalfDays || 0)}</p>
            </div>
            <div className="metric-card">
              <p className="metric-label">Holidays</p>
              <p className="metric-value">{Number(monthlyPayload?.totals?.totalHolidays || 0)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div className="section-card-soft p-4">
              <p className="metric-label">Worked</p>
              <p className="mt-2 text-xl font-bold text-slate-900">{formatMinutes(monthlyPayload?.totals?.totalWorkedMinutes || 0)}</p>
            </div>
            <div className="section-card-soft p-4">
              <p className="metric-label">Late Minutes</p>
              <p className="mt-2 text-xl font-bold text-slate-900">{formatMinutes(monthlyPayload?.totals?.totalLateMinutes || 0)}</p>
            </div>
            <div className="section-card-soft p-4">
              <p className="metric-label">Overtime</p>
              <p className="mt-2 text-xl font-bold text-slate-900">{formatMinutes(monthlyPayload?.totals?.totalOvertimeMinutes || 0)}</p>
            </div>
            <div className="section-card-soft p-4">
              <p className="metric-label">Missing Checkout</p>
              <p className="mt-2 text-xl font-bold text-slate-900">{Number(monthlyPayload?.totals?.missingCheckoutCount || 0)}</p>
            </div>
          </div>

          <div className="table-shell">
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Leave</th>
                    <th>Half-day</th>
                    <th>Holiday</th>
                    <th>Worked</th>
                    <th>Late</th>
                    <th>Overtime</th>
                    <th>Missing Checkout</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingMonthly ? (
                    <tr>
                      <td colSpan={10}>
                        <div className="flex items-center gap-2 py-2 text-slate-500">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading monthly summary...
                        </div>
                      </td>
                    </tr>
                  ) : (monthlyPayload.byStaff || []).length === 0 ? (
                    <tr>
                      <td colSpan={10}>No monthly summary rows found.</td>
                    </tr>
                  ) : (
                    monthlyPayload.byStaff.map((row) => (
                      <tr key={row.staffUserId}>
                        <td className="font-semibold text-slate-900">{row.staffName || `User #${row.staffUserId}`}</td>
                        <td>{row.totalPresentDays}</td>
                        <td>{row.totalAbsentDays}</td>
                        <td>{row.totalLeaveDays}</td>
                        <td>{row.totalHalfDays}</td>
                        <td>{row.totalHolidays}</td>
                        <td>{formatMinutes(row.totalWorkedMinutes)}</td>
                        <td>{formatMinutes(row.totalLateMinutes)}</td>
                        <td>{formatMinutes(row.totalOvertimeMinutes)}</td>
                        <td>{row.missingCheckoutCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      <CorrectionModal
        open={correctionModalOpen}
        row={correctionRow}
        onClose={() => {
          setCorrectionModalOpen(false);
          setCorrectionRow(null);
        }}
        onSave={saveCorrection}
        saving={correctionSaving}
      />
    </div>
  );
}
